import React, { useEffect, useRef, useState } from 'react';
import { useUa } from './UserAdminContext';
import * as api from './userAdminApi';
import Icon from './UaIcon';
import PageHeader from './UaHeader';
import { Avatar, Note, PersonStatus, RoleSelect, StatusPill } from './UaBits';
import {
    EMPTY_FILTERS, anyFilter, fullName, plural, roleName, statusCounts, statusSub, unmanagedMatches, unmanagedSheet, usersSheet, visibleUsers,
} from './uaModel';

/** Users: one row of filters and one table. Role changes save straight from the table. */
export default function UsersTab() {
    const { catalog, data, meId, run, toast, actions, filters: f, setFilters, sel, setSel, flash, unmanaged, openPanel, openModal } = useUa();
    const [pending, setPending] = useState({});
    const allRef = useRef(null);

    // Filtering starts a fresh selection, so nobody acts on rows they can no longer see.
    const setF = (patch) => { setFilters((old) => ({ ...old, ...patch })); setSel(new Set()); };
    const showUnmanaged = f.status === 'unmanaged';
    const list = showUnmanaged ? [] : visibleUsers(data, f);
    const unmanagedList = showUnmanaged ? unmanagedMatches(unmanaged, f.q) : [];
    const ids = list.map((p) => p.id);
    const allOn = ids.length > 0 && ids.every((id) => sel.has(id));
    const someOn = ids.some((id) => sel.has(id));

    useEffect(() => { if (allRef.current) allRef.current.indeterminate = someOn && !allOn; }, [someOn, allOn]);

    const c = statusCounts(data);
    const statusOpts = [['all', 'All statuses', null], ['active', 'Active', c.active], ['deactivated', 'Deactivated', c.deactivated]];
    if (unmanaged.length || showUnmanaged) statusOpts.push(['unmanaged', 'Not set up yet', unmanaged.length]);

    const toggle = (id) => setSel((old) => { const next = new Set(old); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    const toggleAll = (on) => setSel((old) => { const next = new Set(old); ids.forEach((id) => (on ? next.add(id) : next.delete(id))); return next; });

    const changeRole = async (p, roleId) => {
        setPending((old) => ({ ...old, [p.id]: roleId }));
        await run(api.setAccess(p.id, { role: roleId, overrides: {} }), <><b>{fullName(p)}</b> is now {roleName(data, roleId)}.</>);
        setPending((old) => { const next = { ...old }; delete next[p.id]; return next; });
    };

    const bulkRole = async (roleId) => {
        if (!roleId) return;
        const chosen = Array.from(sel);
        const res = await run(api.bulkRole(chosen, roleId), (r) => {
            const n = r.result.changed.length;
            const self = r.result.skipped.length ? ' You can’t change your own role.' : '';
            return n ? <>Gave {plural(n, 'user')} the <b>{roleName(r, roleId)}</b> role.{self}</> : `Nobody needed changing.${self}`;
        }, { undo: (r) => r.result.changed.length > 0 });
        if (res) setSel(new Set());
    };

    const bulkDeactivate = () => {
        const chosen = Array.from(sel).filter((id) => {
            const p = data.people.find((x) => x.id === id);
            return p && id !== meId && p.status !== 'deactivated';
        });
        if (!chosen.length) { toast('Nothing to deactivate. You can’t deactivate yourself.', { kind: 'info' }); return; }
        openModal({ type: 'deact', ids: chosen });
    };

    const exportNow = () => {
        if (showUnmanaged) actions.exportSheet(unmanagedSheet(unmanagedList), 'users-not-set-up', 'user');
        else actions.exportSheet(usersSheet(list, data), 'users', 'user');
    };

    // A plain function, not a component, so the header buttons keep focus when the sort changes.
    const sortHead = (label, k) => {
        const on = f.sort === k;
        return (
            <button type="button" className={`ua-sortb${on ? ' ua-on' : ''}`} onClick={() => setFilters((o) => ({ ...o, sort: k, dir: o.sort === k ? -o.dir : 1 }))}>
                {label}<Icon name={on ? (f.dir === 1 ? 'down' : 'up') : 'updown'} size={12} stroke={2.6} />
            </button>
        );
    };
    const ariaSort = (k) => (f.sort === k ? (f.dir === 1 ? 'ascending' : 'descending') : undefined);

    let body;
    if (showUnmanaged) {
        body = !unmanagedList.length ? (
            <div className="ua-empty"><b>Everyone is set up</b><span>Everyone who signs in to Bridge has a role here.</span></div>
        ) : (
            <>
                <div className="ua-note-wrap">
                    <Note>These people already sign in to Bridge, but don’t have a department or role here yet. Set them up to manage their access.</Note>
                </div>
                <div className="ua-tw">
                    <table className="ua-t">
                        <thead><tr><th className="ua-ck" /><th>Name</th><th>Email</th><th>Department</th><th>Role</th><th>Status</th><th className="ua-acts"><span className="ua-sr">Actions</span></th></tr></thead>
                        <tbody>
                            {unmanagedList.map((u) => (
                                <tr key={u.email}>
                                    <td className="ua-ck" />
                                    <td><div className="ua-person"><Avatar person={u} ghost /><b className="ua-nw">{fullName(u)}</b></div></td>
                                    <td className="ua-mail">{u.email}</td>
                                    <td className={u.dept ? 'ua-depts-cell' : 'ua-muted'}>{u.dept || '—'}</td>
                                    <td className="ua-muted">—</td>
                                    <td><StatusPill status="unmanaged" /><span className="ua-st-sub">Can sign in to Bridge</span></td>
                                    <td className="ua-acts">
                                        <button type="button" className="ua-btn ua-btn-sm ua-btn-primary" onClick={() => openModal({ type: 'setup', person: u })}>Set up</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        );
    } else {
        body = (
            <>
                {unmanaged.length && !anyFilter(f) ? (
                    <Note>
                        {plural(unmanaged.length, 'person', 'people')} in Bridge {unmanaged.length === 1 ? 'isn’t' : 'aren’t'} set up here yet.{' '}
                        <button type="button" className="ua-link" onClick={() => setF({ status: 'unmanaged' })}>Show them</button>
                    </Note>
                ) : null}
                {sel.size ? (
                    <div className="ua-bulk">
                        <b>{sel.size} selected</b>
                        <label className="ua-sr" htmlFor="ua-bulk-role">Change role</label>
                        <select id="ua-bulk-role" className="ua-sel" value="" onChange={(e) => bulkRole(e.target.value)}>
                            <option value="">Change role to…</option>
                            {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                        <button type="button" className="ua-btn ua-btn-sm" onClick={bulkDeactivate}><Icon name="power" size={14} />Deactivate</button>
                        <span className="ua-end"><button type="button" className="ua-link" onClick={() => setSel(new Set())}>Clear selection</button></span>
                    </div>
                ) : null}
                {!list.length ? (
                    <div className="ua-empty">
                        <b>No users match</b>
                        <span>Try another name, or clear the filters.</span>
                        {anyFilter(f) ? <button type="button" className="ua-btn ua-btn-sm" onClick={() => setF({ ...EMPTY_FILTERS, sort: f.sort, dir: f.dir })}>Clear filters</button> : null}
                    </div>
                ) : (
                    <div className="ua-tw">
                        <table className="ua-t">
                            <thead>
                                <tr>
                                    <th className="ua-ck"><input ref={allRef} type="checkbox" checked={allOn} onChange={(e) => toggleAll(e.target.checked)} aria-label="Select all" /></th>
                                    <th aria-sort={ariaSort('name')}>{sortHead('Name', 'name')}</th>
                                    <th>Email</th>
                                    <th aria-sort={ariaSort('dept')}>{sortHead('Department', 'dept')}</th>
                                    <th aria-sort={ariaSort('role')}>{sortHead('Role', 'role')}</th>
                                    <th aria-sort={ariaSort('status')}>{sortHead('Status', 'status')}</th>
                                    <th className="ua-acts"><span className="ua-sr">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((p) => {
                                    const isMe = p.id === meId;
                                    const off = p.status === 'deactivated';
                                    const name = fullName(p);
                                    const cls = [sel.has(p.id) ? 'ua-row-sel' : '', off ? 'ua-row-off' : '', flash === p.id ? 'ua-row-new' : ''].filter(Boolean).join(' ');
                                    return (
                                        <tr key={p.id} className={cls || undefined}>
                                            <td className="ua-ck"><input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} aria-label={`Select ${name}`} /></td>
                                            <td>
                                                <div className="ua-person">
                                                    <Avatar person={p} isMe={isMe} />
                                                    <span className="ua-person-tx">
                                                        <button type="button" className="ua-name" onClick={() => openPanel(p.id)}>{name}{isMe ? <span className="ua-you">You</span> : null}</button>
                                                        <span className="ua-title" title={p.title || undefined}>{p.title || '—'}</span>
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="ua-mail">{p.email}</td>
                                            <td className="ua-depts-cell">{p.dept}</td>
                                            <td>
                                                <RoleSelect
                                                    data={data} value={pending[p.id] || p.role} disabled={isMe || !!pending[p.id]} label={`Role for ${name}`}
                                                    title={isMe ? 'You can’t change your own role' : undefined} onChange={(r) => changeRole(p, r)}
                                                />
                                            </td>
                                            <td><PersonStatus person={p} /><span className="ua-st-sub">{statusSub(p)}</span></td>
                                            <td className="ua-acts">
                                                <button type="button" className="ua-ib" onClick={() => openPanel(p.id)} title="Edit" aria-label={`Edit ${name}`}><Icon name="edit" size={15} /></button>
                                                {off ? (
                                                    <button type="button" className="ua-ib ua-ib-good" onClick={() => openModal({ type: 'react', id: p.id })} title="Reactivate" aria-label={`Reactivate ${name}`}>
                                                        <Icon name="power" size={15} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button" className="ua-ib ua-ib-danger" disabled={isMe} onClick={() => openModal({ type: 'deact', ids: [p.id] })}
                                                        title={isMe ? 'You can’t deactivate yourself' : 'Deactivate'} aria-label={`Deactivate ${name}`}
                                                    >
                                                        <Icon name="power" size={15} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    }

    const shown = showUnmanaged ? unmanagedList.length : list.length;
    return (
        <>
            <PageHeader
                tab="users" title="Users" count={data.people.filter((p) => p.status !== 'deactivated').length}
                sub="Add people, choose their role, and turn off access when they leave."
                actions={(
                    <>
                        <button type="button" className="ua-btn" onClick={exportNow}><Icon name="sheet" />Export to Excel</button>
                        <button type="button" className="ua-btn ua-btn-primary" onClick={() => openModal({ type: 'add' })}><Icon name="plus" />Add user</button>
                    </>
                )}
            />
            <section className="ua-card">
                <div className="ua-toolbar">
                    <label className="ua-search">
                        <span className="ua-sr">Search users</span>
                        <Icon name="search" />
                        <input className="ua-inp" type="search" placeholder="Search by name or email" value={f.q} autoComplete="off" onChange={(e) => setF({ q: e.target.value })} />
                    </label>
                    <select className={`ua-sel${f.dept !== 'all' ? ' ua-on' : ''}`} aria-label="Department" value={f.dept} onChange={(e) => setF({ dept: e.target.value })}>
                        <option value="all">All departments</option>
                        {catalog.departments.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    <select className={`ua-sel${f.role !== 'all' ? ' ua-on' : ''}`} aria-label="Role" value={f.role} onChange={(e) => setF({ role: e.target.value })}>
                        <option value="all">All roles</option>
                        {data.roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                    <select className={`ua-sel${f.status !== 'all' ? ' ua-on' : ''}`} aria-label="Status" value={f.status} onChange={(e) => setF({ status: e.target.value })}>
                        {statusOpts.map((o) => <option key={o[0]} value={o[0]}>{o[1]}{o[2] != null ? ` (${o[2]})` : ''}</option>)}
                    </select>
                    <span className="ua-end">
                        {anyFilter(f) ? <button type="button" className="ua-link" onClick={() => setF({ ...EMPTY_FILTERS, sort: f.sort, dir: f.dir })}>Clear filters</button> : null}
                        <span>{plural(shown, 'user')}</span>
                    </span>
                </div>
                {body}
            </section>
        </>
    );
}
